import type { FeedbackItem, TranscriptSegment } from "@type/interview";
import type {
    BeFeedbackItem,
    BeSpeechMetrics,
    RubricScores,
    TrainingSession,
    TranscriptTurn,
} from "@type/training";
import { RUBRIC_LABELS } from "@type/training";

const RUBRIC_DESCRIPTIONS: Record<keyof RubricScores, string> = {
    empathy:
        "How well you acknowledged how the customer felt and responded with care before solving.",
    problem_resolution:
        "How clearly you understood the issue, proposed a path, and confirmed it solved the problem.",
    product_knowledge:
        "How accurately and confidently you described products, policies, and tradeoffs.",
    tone:
        "How warm, calm, and professional your delivery felt across the call.",
    call_handling:
        "How well you controlled pacing, set expectations, and closed the call cleanly.",
};

export interface SpeechMetrics {
    wordsPerMinute: number;
    fillerPerMinute: number;
    fillerCount: number;
    talkListenRatio: string;
    longestMonologueSec: number;
    totalWords: number;
    isMocked: boolean;
}

export function stripAudioTags(text: string): string {
    return text.replace(/\[.*?\]/g, "").replace(/\s{2,}/g, " ").trim();
}

const FILLER_REGEX =
    /\b(um+|uh+|err+|like|you know|sort of|kind of|i mean|basically|literally|actually)\b/gi;

const WORDS_PER_SECOND_FALLBACK = 2.4;

function countWords(text: string): number {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
}

function formatTimestamp(ts: string | undefined, anchorMs?: number): string {
    if (!ts) return "0:00";
    if (/^\d{1,2}:\d{2}$/.test(ts)) return ts;
    const parsed = Date.parse(ts);
    if (Number.isNaN(parsed)) return ts;
    // If we don't have an anchor, the offset is meaningless — return the raw
    // ts string rather than collapsing every turn to "0:00".
    if (anchorMs === undefined) return ts;
    const offsetSec = Math.max(0, Math.floor((parsed - anchorMs) / 1000));
    const m = Math.floor(offsetSec / 60);
    const s = offsetSec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}

function deriveTitle(text: string, prefix: string): string {
    const trimmed = text.trim();
    if (!trimmed) return prefix;
    const firstSentence = trimmed.split(/[.!?\n]/)[0].trim();
    const short = firstSentence.length > 70
        ? firstSentence.slice(0, 67) + "..."
        : firstSentence;
    return short || prefix;
}

function splitParagraphs(text: string): string[] {
    // Split on newlines first, then on inline bullet markers, then strip any
    // residual leading bullet glyphs so card titles don't show "- foo".
    return text
        .split(/\n+/)
        .flatMap((line) => line.split(/(?:^|\s)[•\-*]\s+/))
        .map((s) => s.replace(/^[•\-*]\s+/, "").trim())
        .filter((s) => s.length > 0);
}

export function transcriptToHighlights(
    turns: TranscriptTurn[] | null | undefined
): TranscriptSegment[] {
    if (!turns || turns.length === 0) return [];
    const anchorMs = (() => {
        const first = turns[0]?.ts;
        if (!first) return undefined;
        const parsed = Date.parse(first);
        return Number.isNaN(parsed) ? undefined : parsed;
    })();

    const out: TranscriptSegment[] = [];
    let pendingPersona: TranscriptTurn | null = null;

    for (const turn of turns) {
        if (turn.speaker === "persona") {
            if (pendingPersona) {
                out.push({
                    time: formatTimestamp(pendingPersona.ts, anchorMs),
                    question: stripAudioTags(pendingPersona.text),
                    answer: "",
                });
            }
            pendingPersona = turn;
        } else {
            out.push({
                time: formatTimestamp(
                    pendingPersona?.ts || turn.ts,
                    anchorMs
                ),
                question: pendingPersona ? stripAudioTags(pendingPersona.text) : "",
                answer: stripAudioTags(turn.text),
            });
            pendingPersona = null;
        }
    }
    if (pendingPersona) {
        out.push({
            time: formatTimestamp(pendingPersona.ts, anchorMs),
            question: stripAudioTags(pendingPersona.text),
            answer: "",
        });
    }
    return out;
}

// Marker so we can detect (and avoid double-stamping) a title that we've
// already prefixed with a BE-supplied timestamp. Using a non-rendering
// zero-width space is brittle; using a printable separator that's unique
// here ("·" plus a regex check on the BE timestamp shape) is good enough.
const TS_TITLE_RE = /^\d{1,2}:\d{2}\s+·\s+/;

function mapBeFeedbackItem(item: BeFeedbackItem): FeedbackItem {
    // FeedbackItem (the shared interview-side type in @type/interview) has
    // no `timestamp` slot, so we fold the BE-supplied timestamp into the
    // title for display. Two safety properties:
    //   1. Empty quote/timestamp strings are treated as absent.
    //      feedback-expandable hides the quote block on a falsy quote.
    //   2. We refuse to prepend twice — if the BE ever changes to include
    //      the timestamp inline in the title, TS_TITLE_RE catches it.
    // If the FeedbackItem type ever grows a dedicated `timestamp?` slot
    // with renderer support, drop the title-fold and pass timestamp
    // through directly.
    const ts = item.timestamp?.trim();
    const titleAlreadyHasTs = TS_TITLE_RE.test(item.title);
    const title = ts && !titleAlreadyHasTs ? `${ts} · ${item.title}` : item.title;
    return {
        category: item.category,
        title,
        quote: item.quote || "",
        content: item.content,
    };
}

export function mapBeSpeechMetrics(be: BeSpeechMetrics): SpeechMetrics {
    // BE may return either integers or floats for the talk:listen split.
    // Round to integers for visual consistency with the other tiles, then
    // re-balance so the displayed pair always sums to 100.
    const empPct = Math.max(0, Math.min(100, Math.round(be.talk_listen_ratio.employee_pct)));
    const perPct = 100 - empPct;
    return {
        wordsPerMinute: Math.round(be.words_per_minute),
        fillerPerMinute: Math.round(be.filler_per_minute * 10) / 10,
        fillerCount: be.filler_count,
        talkListenRatio: `${empPct} : ${perPct}`,
        longestMonologueSec: Math.round(be.longest_monologue_seconds),
        totalWords: be.total_employee_words,
        isMocked: false,
    };
}

export function buildTrainingFeedbacks(
    session: TrainingSession
): FeedbackItem[] {
    // Prefer the structured BE-supplied feedback_items[] when present.
    // Falls through to the legacy text-split path otherwise.
    if (session.feedback_items && session.feedback_items.length > 0) {
        return session.feedback_items.map(mapBeFeedbackItem);
    }

    const items: FeedbackItem[] = [];

    if (session.transcript_highlight) {
        items.push({
            category: "Excellent",
            title: `Coaching Moment · ${session.transcript_highlight.timestamp}`,
            quote: session.transcript_highlight.quote,
            content: session.transcript_highlight.coach_note,
        });
    }

    if (session.feedback_strengths) {
        const parts = splitParagraphs(session.feedback_strengths);
        parts.forEach((part, i) => {
            items.push({
                category: i === 0 ? "Brilliant" : "Excellent",
                title: deriveTitle(part, `Strength ${i + 1}`),
                quote: "",
                content: part,
            });
        });
    }

    if (session.feedback_growth) {
        const parts = splitParagraphs(session.feedback_growth);
        parts.forEach((part, i) => {
            items.push({
                category: "Inaccuracy",
                title: deriveTitle(part, `Growth area ${i + 1}`),
                quote: "",
                content: part,
            });
        });
    }

    if (session.rubric_scores) {
        (Object.keys(RUBRIC_LABELS) as (keyof RubricScores)[]).forEach((k) => {
            const v = session.rubric_scores![k];
            const category: FeedbackItem["category"] =
                v >= 5
                    ? "Brilliant"
                    : v >= 4
                        ? "Excellent"
                        : v >= 3
                            ? "Good"
                            : v >= 2
                                ? "Inaccuracy"
                                : "Mistake";
            items.push({
                category,
                title: `${RUBRIC_LABELS[k]} · ${v}/5`,
                quote: "",
                content: RUBRIC_DESCRIPTIONS[k],
            });
        });
    }

    return items;
}

// Allowlist of host suffixes from which we'll render a recording <video>.
// Anything else gets refused — protects against a compromised BE response
// pointing the player at an arbitrary cross-origin URL.
const RECORDING_HOST_ALLOWLIST = [
    ".amazonaws.com",      // S3 + CloudFront S3-region origins
    ".cloudfront.net",     // standalone CloudFront distributions
    ".daily.co",           // Daily.co recording links
    ".ourteam.ai",         // ourteam-hosted CDNs / proxies
];

/**
 * Validate a BE-supplied recording URL before handing it to a <video src>.
 * Refuses non-HTTPS URLs and origins not in the allowlist. Returns null on
 * any failure so the caller can fall through to the placeholder UI.
 */
export function getSafeRecordingUrl(raw: string | null | undefined): string | null {
    if (!raw) return null;
    let parsed: URL;
    try {
        parsed = new URL(raw);
    } catch {
        return null;
    }
    if (parsed.protocol !== "https:") return null;
    const host = parsed.hostname.toLowerCase();
    const allowed = RECORDING_HOST_ALLOWLIST.some(
        (suffix) => host === suffix.replace(/^\./, "") || host.endsWith(suffix)
    );
    if (!allowed) return null;
    return parsed.toString();
}

/**
 * Returns true when the URL points to a different origin than the document.
 * Caller can use this to decide whether to set crossOrigin="anonymous".
 * Always false during SSR or when window is unavailable.
 */
export function isCrossOriginRecording(safeUrl: string): boolean {
    if (typeof window === "undefined") return false;
    try {
        const recOrigin = new URL(safeUrl).origin;
        return recOrigin !== window.location.origin;
    } catch {
        return false;
    }
}

export function buildSpeechMetrics(session: TrainingSession): SpeechMetrics {
    // Prefer BE-supplied metrics when present — drops the "sample" badge.
    if (session.speech_metrics) {
        return mapBeSpeechMetrics(session.speech_metrics);
    }

    const turns = session.transcript_full ?? session.transcript ?? [];
    const employeeTurns = turns.filter((t) => t.speaker === "employee");
    const personaTurns = turns.filter((t) => t.speaker === "persona");
    const employeeText = employeeTurns.map((t) => t.text).join(" ");
    const personaText = personaTurns.map((t) => t.text).join(" ");
    const employeeWords = countWords(employeeText);
    const personaWords = countWords(personaText);
    const fillerMatches = employeeText.match(FILLER_REGEX) || [];

    const startedAt = session.started_at
        ? Date.parse(session.started_at)
        : NaN;
    const completedAt = session.completed_at
        ? Date.parse(session.completed_at)
        : NaN;
    const measuredMinutes =
        Number.isFinite(startedAt) && Number.isFinite(completedAt)
            ? Math.max(0.5, (completedAt - startedAt) / 60000)
            : 0;
    const estimatedMinutes =
        employeeWords > 0
            ? Math.max(0.5, employeeWords / (WORDS_PER_SECOND_FALLBACK * 60))
            : 0;
    const durationMin = measuredMinutes || estimatedMinutes;

    const isMocked = employeeWords < 20;

    if (isMocked) {
        // Sample numbers used when the transcript is too thin to compute. Keep
        // fillerCount and totalWords internally consistent (≈4.2 fillers/min ×
        // 2.1min ≈ 9; 148 wpm × 2.1min ≈ 311 words) so any future tile that
        // surfaces them doesn't look contradictory.
        return {
            wordsPerMinute: 148,
            fillerPerMinute: 4.2,
            fillerCount: 9,
            talkListenRatio: "58 : 42",
            longestMonologueSec: 38,
            totalWords: 311,
            isMocked: true,
        };
    }

    const totalWords = employeeWords + personaWords;
    const employeePct =
        totalWords > 0 ? Math.round((employeeWords / totalWords) * 100) : 50;
    const personaPct = 100 - employeePct;

    const longestMonologueSec = employeeTurns.reduce((max, t) => {
        const w = countWords(t.text);
        const sec = Math.round(w / WORDS_PER_SECOND_FALLBACK);
        return sec > max ? sec : max;
    }, 0);

    return {
        wordsPerMinute: Math.round(employeeWords / durationMin),
        fillerPerMinute: Math.round((fillerMatches.length / durationMin) * 10) / 10,
        fillerCount: fillerMatches.length,
        talkListenRatio: `${employeePct} : ${personaPct}`,
        longestMonologueSec,
        totalWords: employeeWords,
        isMocked: false,
    };
}
